import React, { useState, useEffect } from 'react';
import type { User } from '@/types/user';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Lock,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import type { UpdateProfileData  } from '@/types/user';
import { userService } from '@/services/userService';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface AccountSecurityProps {
  user: User;
}

export const AccountSecurity: React.FC<AccountSecurityProps> = ({ user }) => {
  const { updateUser } = useUser();
  const navigate = useNavigate();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [emailData, setEmailData] = useState({
    email: user.email || '',
  });

  const [phoneData, setPhoneData] = useState({
    phone_code: user.phone_code || user.mobile_region_code || '',
    phone: user.phone || user.mobile_number || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  // Helper function to combine phone code and number for display
  const getDisplayPhone = () => {
    const code = phoneData.phone_code || '';
    const number = phoneData.phone || '';
    if (code && number) {
      return `${code}${number}`;
    }
    return number || code || '';
  };

  // Update form data when user changes
  useEffect(() => {
    setEmailData({
      email: user.email || '',
    });
    setPhoneData({
      phone_code: user.phone_code || user.mobile_region_code || '',
      phone: user.phone || user.mobile_number || '',
    });
  }, [user]);

  const handleEmailChange = (value: string) => {
    setEmailData({ email: value });
  };

  const handlePhoneChange = (value: string) => {
    // Parse phone number with region code (format: "+60 123456789" or "+60123456789")
    if (value.startsWith('+')) {
      // Remove the + and try to split by space
      const withoutPlus = value.substring(1);
      const parts = withoutPlus.split(/\s+/);
      
      if (parts.length >= 2) {
        // Has space: "+60 123456789"
        setPhoneData({
          phone_code: `+${parts[0]}`,
          phone: parts.slice(1).join(' '),
        });
      } else {
        // No space: "+60123456789" - try to detect region code (typically 1-4 digits)
        // Common region codes: 1-3 digits (e.g., +1, +44, +60, +852)
        const match = withoutPlus.match(/^(\d{1,4})(\d+)$/);
        if (match) {
          setPhoneData({
            phone_code: `+${match[1]}`,
            phone: match[2],
          });
        } else {
          // Can't parse, store as phone number only
          setPhoneData(prev => ({ ...prev, phone: value }));
        }
      }
    } else if (value.trim() === '') {
      // Empty input - clear both
      setPhoneData({ phone_code: '', phone: '' });
    } else {
      // Simple number input without + - store as phone number only
      setPhoneData(prev => ({ ...prev, phone: value }));
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveEmail = async () => {
    setIsLoading(true);
    try {
      const updateData: UpdateProfileData = {
        first_name: user.first_name,
        email: emailData.email,
      };

      const response = await userService.updateProfile(updateData);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Ensure user data exists
      if (!response.user) {
        throw new Error('User data not returned from server');
      }
      
      // Update user context
      const updatedUser: User = {
        ...user,
        ...response.user,
        // Map backend fields to frontend User type
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        mobile_region_code: response.user.mobile_region_code,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
      };
      updateUser(updatedUser);

      toast({
        title: "Success",
        description: "Email updated successfully!",
      });

    } catch (error: any) {
      console.error('Failed to update email:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePhone = async () => {
    setIsLoading(true);
    try {
      const updateData: UpdateProfileData = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone_code: phoneData.phone_code || undefined,
        phone: phoneData.phone || undefined,
      };

      const response = await userService.updateProfile(updateData);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Ensure user data exists
      if (!response.user) {
        throw new Error('User data not returned from server');
      }
      
      // Update user context
      const updatedUser: User = {
        ...user,
        ...response.user,
        // Map backend fields to frontend User type
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        mobile_region_code: response.user.mobile_region_code,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
      };
      updateUser(updatedUser);

      toast({
        title: "Success",
        description: "Phone number updated successfully!",
      });

    } catch (error: any) {
      console.error('Failed to update phone:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update phone number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setIsLoading(true);
    try {
      const updateData: UpdateProfileData = {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        current_password: passwordData.current_password,
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation,
      };

      const response = await userService.updateProfile(updateData);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Ensure user data exists
      if (!response.user) {
        throw new Error('User data not returned from server');
      }
      
      // Update user context
      const updatedUser: User = {
        ...user,
        ...response.user,
        // Map backend fields to frontend User type
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        mobile_region_code: response.user.mobile_region_code,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
      };
      updateUser(updatedUser);

      toast({
        title: "Success",
        description: "Password updated successfully!",
      });

      // Reset password form
      setPasswordData({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
      setIsChangingPassword(false);
    } catch (error: any) {
      console.error('Failed to update password:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Card className="rounded-lg shadow-inner border-0 account-security-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 profile-typography">
          Account Security
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Address */}
        <div className="space-y-2">
          <label className="text-white font-medium profile-label">Email Address</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1 min-w-0">
              <Input
                value={emailData.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="account-security-input account-security-input-field text-white placeholder:text-gray-400"
                placeholder="Enter email address"
              />
            </div>
            <Button
              onClick={handleSaveEmail}
              disabled={isLoading}
              className="text-white profile-typography account-security-button account-security-save-button w-full sm:w-[200px] sm:flex-shrink-0"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Email'}
            </Button>
          </div>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <label className="text-white font-medium profile-label">Phone Number</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1 min-w-0">
              <Input
                value={getDisplayPhone()}
                onChange={(e) => handlePhoneChange(e.target.value)}
                className="account-security-input account-security-input-field text-white placeholder:text-gray-400"
                placeholder="Enter phone number (e.g., +60 123456789)"
              />
            </div>
            <Button
              onClick={handleSavePhone}
              disabled={isLoading}
              className="text-white profile-typography account-security-button account-security-phone-button w-full sm:w-[200px] sm:flex-shrink-0"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Phone Number'}
            </Button>
          </div>
        </div>

        {/* Password Section */}
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-white font-medium profile-label">Password</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <p className="text-white profile-typography flex-1 max-w-full">Set a new password for your account.</p>
              <Button
                onClick={() => navigate('/update-password')}
                className="text-white profile-typography account-security-password-button w-full sm:w-[200px] sm:flex-shrink-0"
              >
                Change Password
              </Button>
            </div>
          </div>

          {isChangingPassword && (
            <div className="space-y-4 password-form-container">
              {/* Current Password */}
              <div className="space-y-2">
                <label className="text-white font-medium profile-label">Current Password</label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                    className="profile-input text-white placeholder:text-gray-400"
                    placeholder="Enter current password"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <label className="text-white font-medium profile-label">New Password</label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.password}
                    onChange={(e) => handlePasswordChange('password', e.target.value)}
                    className="profile-input text-white placeholder:text-gray-400"
                    placeholder="Enter new password"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label className="text-white font-medium profile-label">Confirm New Password</label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.password_confirmation}
                    onChange={(e) => handlePasswordChange('password_confirmation', e.target.value)}
                    className="profile-input text-white placeholder:text-gray-400"
                    placeholder="Confirm new password"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Save Password Button */}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={isLoading || !passwordData.current_password || !passwordData.password || !passwordData.password_confirmation}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 profile-typography"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
